/**
 * The history view (design §11 screen 3's "link to history view") — every period this customer has ever
 * had (open + closed) and the full audit timeline (human actions, system/expiry-job writes, and sweep
 * observations for their email). Read-only.
 */
import { useTranslation } from "react-i18next";
import { EmptyState, Modal, SkeletonRows, StatusPill } from "@/components/ui";
import { PoolErrorState } from "@/features/pool/PoolErrorState";
import { usePoolCustomerHistory } from "@/features/pool/hooks";
import { formatDate, formatInstant } from "@/lib/format";

export function CustomerHistoryModal({ email, onClose }: { email: string; onClose: () => void }) {
  const { t } = useTranslation();
  const historyQ = usePoolCustomerHistory(email);

  return (
    <Modal open onClose={onClose} title={t("pool.period.historyTitle", { email })} size="lg">
      {historyQ.isPending ? (
        <SkeletonRows rows={4} />
      ) : historyQ.isError ? (
        <PoolErrorState error={historyQ.error} onRetry={() => void historyQ.refetch()} />
      ) : (
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-2">
            <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
              {t("pool.period.historyPeriodsTitle")}
            </h3>
            {historyQ.data.periods.length === 0 ? (
              <EmptyState title={t("pool.period.historyEmpty")} />
            ) : (
              <ul className="flex flex-col gap-2">
                {historyQ.data.periods.map((p) => (
                  <li
                    key={p.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-(--radius) border border-(--border) bg-(--surface) px-3 py-2.5"
                  >
                    <span className="text-(length:--text-sm) text-(--text)">
                      {formatDate(p.period_start)} – {formatDate(p.period_end)}
                    </span>
                    <StatusPill tone={p.closed_at ? "neutral" : "ok"}>
                      {p.closed_at
                        ? t("pool.period.historyPeriodClosed", { reason: p.close_reason ?? "—" })
                        : t("pool.period.historyPeriodOpen")}
                    </StatusPill>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
              {t("pool.period.historyAuditTitle")}
            </h3>
            {historyQ.data.audit.length === 0 ? (
              <EmptyState title={t("pool.period.historyEmpty")} />
            ) : (
              <ol className="flex flex-col gap-2 border-l border-(--border) pl-4">
                {historyQ.data.audit.map((a) => (
                  <li key={a.id} className="text-(length:--text-xs)">
                    <span className="font-medium text-(--text)">{a.action}</span>
                    <span className="ml-2 text-(--text-subtle)">{formatInstant(a.ts)}</span>
                    <span className="ml-2 text-(--text-muted)">{a.actor}</span>
                    {a.error && <p className="mt-0.5 text-(--danger)">{a.error}</p>}
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}
